/**
 * @module ui/components/loading-indicator
 * @description Progressive loading indicators with status messages
 */

import { getElement } from '../dom/dom-manager.js';
import { logger } from '../../utils/logger.js';

/**
 * Loading state types
 */
export const LOADING_STATES = {
    INITIALIZING: 'initializing',
    LOADING_METADATA: 'loading-metadata',
    LOADING_FILES: 'loading-files',
    PROCESSING: 'processing',
    COMPLETE: 'complete'
};

/**
 * Loading messages for different states
 */
const LOADING_MESSAGES = {
    [LOADING_STATES.INITIALIZING]: 'Initializing...',
    [LOADING_STATES.LOADING_METADATA]: 'Loading metadata...',
    [LOADING_STATES.LOADING_FILES]: 'Loading files...',
    [LOADING_STATES.PROCESSING]: 'Processing...',
    [LOADING_STATES.COMPLETE]: 'Complete!'
};

/**
 * Show loading indicator with optional message
 * @param {string} [state=LOADING_STATES.INITIALIZING] - Loading state
 * @param {string} [customMessage] - Optional custom message
 */
export function showLoading(state = LOADING_STATES.INITIALIZING, customMessage = null) {
    const loadingSection = getElement('loadingSection');
    if (!loadingSection) {
        logger.warn('Loading section element not found');
        return;
    }

    const message = customMessage || LOADING_MESSAGES[state] || LOADING_MESSAGES[LOADING_STATES.INITIALIZING];

    // Update loading message if paragraph exists
    const loadingText = loadingSection.querySelector('p');
    if (loadingText) {
        loadingText.textContent = message;
    }

    loadingSection.classList.remove('hidden');
    logger.debug(`Loading indicator shown: ${message}`);
}

/**
 * Hide loading indicator
 */
export function hideLoading() {
    const loadingSection = getElement('loadingSection');
    if (!loadingSection) {
        return;
    }

    loadingSection.classList.add('hidden');
    logger.debug('Loading indicator hidden');
}

/**
 * Update loading progress with message
 * @param {string} message - Progress message to display
 */
export function updateLoadingMessage(message) {
    const loadingSection = getElement('loadingSection');
    if (!loadingSection) {
        return;
    }

    const loadingText = loadingSection.querySelector('p');
    if (loadingText) {
        loadingText.textContent = message;
        logger.debug(`Loading message updated: ${message}`);
    }
}

/**
 * Show loading with progress tracking
 * @param {string} operation - Operation name
 * @param {number} current - Current progress (0-100)
 * @param {number} total - Total items
 * @param {string} [unit='items'] - Unit of measurement
 */
export function showLoadingProgress(operation, current, total, unit = 'items') {
    const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
    const message = `${operation}: ${current} of ${total} ${unit} (${percentage}%)`;
    updateLoadingMessage(message);
}

/**
 * Create a loading tracker for multi-step operations
 * @param {Array<string>} steps - Array of step names
 * @returns {Object} Tracker with next() and complete() methods
 */
export function createLoadingTracker(steps) {
    let currentStep = 0;

    return {
        /**
         * Move to next step
         */
        next() {
            if (currentStep < steps.length) {
                const step = steps[currentStep];
                updateLoadingMessage(step);
                currentStep++;
            }
        },

        /**
         * Mark as complete
         */
        complete() {
            updateLoadingMessage('Complete!');
            setTimeout(() => {
                hideLoading();
            }, 500);
        },

        /**
         * Get current step index
         */
        getCurrentStep() {
            return currentStep;
        },

        /**
         * Get total steps
         */
        getTotalSteps() {
            return steps.length;
        }
    };
}

/**
 * Show inline loading spinner for specific elements
 * @param {HTMLElement} element - Element to show loading in
 * @param {string} [message='Loading...'] - Loading message
 */
export function showInlineLoading(element, message = 'Loading...') {
    if (!element) return;

    const spinner = document.createElement('div');
    spinner.className = 'inline-spinner';
    spinner.innerHTML = `
        <div class="spinner-small"></div>
        <span>${message}</span>
    `;

    element.appendChild(spinner);
}

/**
 * Hide inline loading spinner
 * @param {HTMLElement} element - Element containing the spinner
 */
export function hideInlineLoading(element) {
    if (!element) return;

    const spinner = element.querySelector('.inline-spinner');
    if (spinner) {
        spinner.remove();
    }
}

/**
 * Disable button and show loading state
 * @param {HTMLElement} button - Button element
 * @param {string} [loadingText='Loading...'] - Text to show while loading
 * @returns {Function} Function to restore button state
 */
export function setButtonLoading(button, loadingText = 'Loading...') {
    if (!button) return () => {};

    const originalText = button.textContent;
    const wasDisabled = button.disabled;

    button.disabled = true;
    button.textContent = loadingText;
    button.classList.add('btn-loading');

    // Return restore function
    return () => {
        button.disabled = wasDisabled;
        button.textContent = originalText;
        button.classList.remove('btn-loading');
    };
}

export default {
    showLoading,
    hideLoading,
    updateLoadingMessage,
    showLoadingProgress,
    createLoadingTracker,
    showInlineLoading,
    hideInlineLoading,
    setButtonLoading,
    LOADING_STATES
};
